import { useCallback, useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDrainSlitComplaintReportStore } from "./useDrainSlitComplaintReportStore";
import { useColonyStore } from "../../../OrganizationColony/store/useColonyStore";
import DrainSlitComplaintMapView from "./DrainSlitComplaintMapView";

const toWardOption = (ward) => ({
  value: String(ward.circleId ?? ward.wardId ?? ""),
  label: ward.circleName ?? ward.wardName ?? ward.wardNo ?? "",
});

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const ComplaintDetailsMapSection = ({
  latitude,
  longitude,
  initialAddress,
  onLocationSelect,
}) => (
  <>
    <h6
      className="mb-3 mt-2"
      style={{
        backgroundColor: "rgb(235, 240, 247)",
        color: "#003366",
        padding: "8px 12px",
        borderRadius: "4px",
        fontWeight: 600,
      }}
    >
      Map View
    </h6>

    <DrainSlitComplaintMapView
      latitude={latitude}
      longitude={longitude}
      initialAddress={initialAddress}
      height="320px"
      onLocationSelect={onLocationSelect}
    />
  </>
);

const ComplaintDetailsPopup = () => {
  const {
    wards,
    selectedComplaint,
    isUpdatingComplaint,
    complaintStatuses,
    setViewComplaintDetailsModal,
    updateDrainSiltComplaint,
    fetchComplaintStatuses,
  } = useDrainSlitComplaintReportStore();
  const { colonies, fetchColonies } = useColonyStore();
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState("");

  const row = selectedComplaint || {};
  const setFieldValueRef = useRef(null);
  const formAddressRef = useRef("");

  useEffect(() => {
    fetchColonies();
    fetchComplaintStatuses();
  }, [fetchColonies, fetchComplaintStatuses]);

  useEffect(() => {
    setUploadPreviewUrl("");
  }, [selectedComplaint?.complaintId, selectedComplaint?.complaintNo]);

  const handleUploadChange = async (event, setFieldValue, setFieldTouched) => {
    const file = event.target.files?.[0] || null;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (
      file &&
      !allowedTypes.includes(file.type) &&
      !/\.(jpe?g|png)$/i.test(file.name)
    ) {
      toast.error("Only JPG, JPEG, and PNG images are allowed");
      event.target.value = "";
      setFieldValue("uploadFile", null, true);
      setFieldTouched("uploadFile", true, false);
      setUploadPreviewUrl("");
      return;
    }

    setFieldValue("uploadFile", file, true);
    setFieldTouched("uploadFile", true, false);

    if (!file) {
      setUploadPreviewUrl("");
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setUploadPreviewUrl(typeof dataUrl === "string" ? dataUrl : "");
    } catch (error) {
      console.error("Failed to create upload preview:", error);
      setUploadPreviewUrl("");
      toast.error("Unable to preview selected image.");
    }
  };
  const validationSchema = Yup.object().shape({
    complaintNo: Yup.string().trim().required("Complaint Number is required."),
    revisedWardId: Yup.string().required("Revised Ward is required."),
    colonyId: Yup.string().required("Revised Colony is required."),
    status: Yup.string().required("Status is required."),
    address: Yup.string()
      .trim()
      .required("Address is required.")
      .max(500, "Address cannot exceed 500 characters."),
    uploadFile: Yup.mixed().required("Upload File is required."),
    actionRemarks: Yup.string()
      .trim()
      .required("Action Remarks is required.")
      .min(10, "Action Remarks should contain minimum 10 characters.")
      .max(1000, "Action Remarks cannot exceed 1000 characters."),
  });

  const initialValues = {
    complaintNo: row.complaintNo || "",
    revisedWardId: row.revisedWardId != null ? String(row.revisedWardId) : "",
    colonyId: row.revisedColonyId != null ? String(row.revisedColonyId) : "",
    status: row.status || "",
    actionRemarks: row.actionRemarks || "",
    uploadFile: null,
    address: row.revisedAddress || row.address || "",
    latitude: row.revisedLatitude ?? row.latitude ?? "",
    longitude: row.revisedLongitude ?? row.longitude ?? "",
  };

  const onSubmit = async (values) => {
    await updateDrainSiltComplaint(values);
  };

  const handleLocationSelect = useCallback((lat, lng, addr, forceAddress = false) => {
    const setFieldValue = setFieldValueRef.current;
    if (!setFieldValue) return;

    setFieldValue("latitude", lat);
    setFieldValue("longitude", lng);
    if (addr && (forceAddress || !formAddressRef.current?.trim())) {
      setFieldValue("address", addr);
    }
  }, []);

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} enableReinitialize onSubmit={onSubmit}>
      {({ values, setFieldValue, setFieldTouched }) => {
        setFieldValueRef.current = setFieldValue;
        formAddressRef.current = values.address || "";
        const wardColonies = colonies.filter(
          (colony) =>
            !values.revisedWardId ||
            String(colony.circleId) === String(values.revisedWardId)
        );

        const hasColony = wardColonies.some(
          (colony) => String(colony.colonyId) === String(values.colonyId)
        );

        return (
          <Form>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Complaint Number <span className="text-danger">*</span></label>
                <Field name="complaintNo" className="form-control bg-light" readOnly />
                <ErrorMessage className="text-danger" name="complaintNo" component="div" style={{ fontSize: 12 }} />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Revised Ward <span className="text-danger">*</span></label>
                <Select
                  inputId="revisedWardId"
                  name="revisedWardId"
                  classNamePrefix="react-select"
                  options={wards.map(toWardOption)}
                  value={
                    wards
                      .map(toWardOption)
                      .find((option) => option.value === String(values.revisedWardId)) ||
                    null
                  }
                  onChange={(selected) => {
                    setFieldValue("revisedWardId", selected ? selected.value : "", true);
                    setFieldTouched("revisedWardId", true, false);
                    setFieldValue("colonyId", "");
                  }}
                  placeholder="Select Ward"
                  isClearable={false}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <ErrorMessage className="text-danger" name="revisedWardId" component="div" style={{ fontSize: 12 }} />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Revised Colony <span className="text-danger">*</span>
                </label>
                <Select
                  inputId="colonyId"
                  name="colonyId"
                  classNamePrefix="react-select"
                  options={[
                    ...(!hasColony && values.colonyId
                      ? [
                          {
                            value: String(values.colonyId),
                            label:
                              row.revisedColonyName || row.colonyName || values.colonyId,
                          },
                        ]
                      : []),
                    ...wardColonies.map((colony) => ({
                      value: String(colony.colonyId),
                      label: colony.colonyName,
                    })),
                  ]}
                  value={
                    [
                      ...(!hasColony && values.colonyId
                        ? [
                            {
                              value: String(values.colonyId),
                              label:
                                row.revisedColonyName || row.colonyName || values.colonyId,
                            },
                          ]
                        : []),
                      ...wardColonies.map((colony) => ({
                        value: String(colony.colonyId),
                        label: colony.colonyName,
                      })),
                    ].find((option) => option.value === String(values.colonyId)) || null
                  }
                  onChange={(selected) => {
                    setFieldValue("colonyId", selected ? selected.value : "", true);
                    setFieldTouched("colonyId", true, false);
                  }}
                  placeholder="Select Colony"
                  isClearable={false}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <ErrorMessage className="text-danger" name="colonyId" component="div" style={{ fontSize: 12 }} />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Status <span className="text-danger">*</span>
                </label>
                <Field as="select" name="status" className="form-select bg-white">
                  <option value="">Select Status</option>
                  {complaintStatuses
                    .filter((status) => ["Closed", "Invalid"].includes(status.complaintStatusName))
                    .map((status) => (
                    <option
                      key={status.complaintStatusId}
                      value={status.complaintStatusName}
                    >
                      {status.complaintStatusName}
                    </option>
                  ))}
                </Field>
                <ErrorMessage className="text-danger" name="status" component="div" style={{ fontSize: 12 }} />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Upload File <span className="text-danger">*</span>{" "}
                  <small className="text-muted fst-italic" style={{ fontSize: 11 }}>
                    (JPG, JPEG, PNG only)
                  </small>
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
                  onChange={(e) => handleUploadChange(e, setFieldValue, setFieldTouched)}
                />
                <ErrorMessage className="text-danger" name="uploadFile" component="div" style={{ fontSize: 12 }} />
                {uploadPreviewUrl ? (
                  <img
                    src={uploadPreviewUrl}
                    alt="Complaint"
                    style={{
                      marginTop: 8,
                      width: 80,
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 4,
                      border: "1px solid #E0E0E0",
                    }}
                  />
                ) : null}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Address <span className="text-danger">*</span>
                </label>
                <Field
                  as="textarea"
                  name="address"
                  rows="4"
                  className="form-control bg-white"
                  placeholder="Enter address"
                  maxLength={500}
                />
                <div className="d-flex justify-content-between">
                  <ErrorMessage className="text-danger" name="address" component="div" style={{ fontSize: 12 }} />
                  <small className="text-muted">{values.address?.length || 0}/500</small>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Action Remarks <span className="text-danger">*</span>
                </label>
                <Field
                  as="textarea"
                  name="actionRemarks"
                  rows="3"
                  className="form-control bg-white"
                  placeholder="Enter Action Remarks"
                  maxLength={1000}
                />
                <div className="d-flex justify-content-between">
                  <ErrorMessage className="text-danger" name="actionRemarks" component="div" style={{ fontSize: 12 }} />
                  <small className="text-muted">{values.actionRemarks?.length || 0}/1000</small>
                </div>
              </div>
            </div>

            <ComplaintDetailsMapSection
              latitude={values.latitude}
              longitude={values.longitude}
              initialAddress={row.revisedAddress || row.address || ""}
              onLocationSelect={handleLocationSelect}
            />

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setViewComplaintDetailsModal(false)}
                disabled={isUpdatingComplaint}
                style={{
                  padding: "8px 20px",
                  borderRadius: "4px",
                  border: "1px solid #1e40af",
                  background: "#ffffff",
                  color: "#1e40af",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdatingComplaint}
                style={{
                  padding: "8px 20px",
                  borderRadius: "4px",
                  border: "1px solid var(--btn-primary-bg)",
                  background: "var(--btn-primary-bg)",
                  color: "var(--btn-primary-text)",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                {isUpdatingComplaint ? "Saving..." : "Save"}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ComplaintDetailsPopup;
